/**
 * @file Reads a `chunked` attachment version back into one verified `Blob`:
 * fetch the manifest, refuse what this client cannot honour, fetch every chunk
 * with a bounded pool, verify each against its digest and length, assemble in
 * manifest order, then check the total against `fileSize`. The single
 * implementation of the reader protocol on the web side — reached through
 * `resolveAttachmentVersionSource`, which owns the `layout` switch for both the
 * download funnel and the preview overlay.
 *
 * Progress is reported in bytes: once when the manifest is accepted, so the
 * caller learns the real total before any chunk lands, then once per verified
 * chunk. It only ever rises and never exceeds the total, and it stops once the
 * read has failed or been cancelled — though not to the microsecond. An abort
 * landing between the last cancellation check and the manifest-accepted call
 * still delivers that call, and a chunk verified in the same turn as another
 * chunk's failure can report before that failure is observed. Every figure is
 * true of bytes actually read, so a caller has to tolerate one late event,
 * never a wrong one.
 *
 * Cancellation is cooperative, and it has to be: `getBytes` takes no
 * `AbortSignal` and exposes no cancel, so a read already on the wire cannot be
 * stopped. What the signal does stop, the instant it fires: no further chunk is
 * taken, the read rejects with reason `cancelled` without waiting for anything
 * in flight, and every chunk retained so far is dropped. What it does not stop:
 * the at most `CHUNK_FETCH_CONCURRENCY` reads already issued, which run to
 * completion in the background and are discarded on arrival, so the waste is
 * bounded to that many chunks. Real socket cancellation would mean resolving a
 * download URL per chunk and using `fetch(url, { signal })` — one extra round
 * trip per chunk and a departure from the SDK's auth path — which is not worth
 * it for a bound this small.
 *
 * Two honest limitations, both deliberate:
 *
 * `contentMd5` is NOT re-verified here. The manifest's whole-file digest is
 * base64 md5, WebCrypto has no md5, and this repo ships no md5 to the browser —
 * adding a dependency for it is out of this phase's scope. What is verified
 * instead is strictly stronger than any download performs today: the sha256 of
 * every chunk, which covers every byte of the file, plus the assembled total
 * against `fileSize`, which pins the length and therefore the chunk count and
 * order. A forged assembly would have to collide sha256 on a chunk.
 *
 * The whole file is held at once, so `MAX_ASSEMBLED_ATTACHMENT_BYTES` caps what
 * this path accepts. Every chunk is retained until the assembly is complete —
 * one `Blob` per chunk, in manifest order — because a `Blob` is what
 * `new Blob(parts)` needs and there is nowhere to stream to. What the
 * `ArrayBuffer` → `Blob` conversion buys is not a smaller total but a movable
 * one: only `CHUNK_FETCH_CONCURRENCY` buffers are JS heap at any moment, and the
 * retained blobs live in the browser's blob store, which may page them to disk
 * rather than counting against the tab's heap limit. That is the whole
 * justification for a ceiling as generous as 2 GiB. The design's answer for
 * larger files is the File System Access API (`showSaveFilePicker`) or a service
 * worker; this repo has neither, and building one is its own piece of work.
 * The platform offers no way to free a `Blob` explicitly: on every exit —
 * success, failure or cancel — the reader drops its own references, and the
 * blob store reclaims each chunk when the browser next collects garbage.
 */
import { getBytes, ref } from 'firebase/storage';
import {
  MAX_ATTACHMENT_MANIFEST_BYTES,
  MAX_STORED_ATTACHMENT_CHUNK_BYTES,
  attachmentChunkPath,
  attachmentManifestPath,
  parseAttachmentChunkManifest,
  type AttachmentChunkEntry,
  type AttachmentChunkManifest,
} from '#types';
import { storage } from '../../services/startStorage';
import { ChunkedAttachmentError } from './chunkedAttachmentError';
import { verifyChunkBytes } from './verifyChunkBytes';

/**
 * Chunk fetches in flight at once. Six sits at the low end of the design's
 * six-to-twelve range: it already saturates a downlink far better than today's
 * single stream, and every extra slot costs one more chunk resident in the JS
 * heap. Raise it only together with the memory note in this file's header.
 */
export const CHUNK_FETCH_CONCURRENCY = 6;

/**
 * Largest version this path will assemble: 2 GiB. The ceiling exists because
 * the assembly is a `Blob`, not a file handle — past this size a tab is at real
 * risk of dying mid-download, and a clear refusal is a better outcome than a
 * crash. It is generous on purpose: the chunker's own guidance tops out around
 * 1 GB files.
 */
export const MAX_ASSEMBLED_ATTACHMENT_BYTES = 2 * 1024 * 1024 * 1024;

/**
 * How far a chunked read has come. `bytesTransferred` is the sum of the
 * verified chunks retained so far, so it only rises and never exceeds
 * `totalBytes`, which is the manifest's `fileSize`. Both are 0 for an empty
 * file, so a caller dividing them must guard the zero.
 */
export interface ChunkedAttachmentReadProgress {
  /** Bytes of verified chunks retained so far. */
  bytesTransferred: number;
  /** The version's `fileSize`, known once the manifest is accepted. */
  totalBytes: number;
}

/** What the reader needs to turn one chunked version into bytes. */
export interface ChunkedAttachmentVersionRequest {
  /** The attachment document id; chunk paths hang off it. */
  fileUid: string;
  /** The version whose bytes are wanted; the manifest must be that version's own. */
  versionId: string;
  /** The version's `manifestPath`, which is present exactly when it is chunked. */
  manifestPath: string;
  /** Named in error messages only — the reader never checks it against anything. */
  fileName: string;
  /** MIME type stamped on the returned blob, taken from the version's `type`. */
  contentType: string;
  /**
   * Cooperative cancellation. The moment it fires the read rejects with reason
   * `cancelled`, takes no further chunk, and drops every chunk retained so far.
   * Reads already in flight cannot be stopped and are discarded when they land.
   * A signal that has already fired rejects before anything is fetched.
   */
  signal?: AbortSignal;
  /**
   * Called once when the manifest is accepted, with `bytesTransferred` 0 and the
   * real total, then once per verified chunk. It stops once the read has failed
   * or been cancelled, but not to the microsecond — see this file's header for
   * the two windows that deliver one last event. Runs on the reader's turn, so
   * it must not throw.
   */
  onProgress?: (progress: ChunkedAttachmentReadProgress) => void;
}

const describeGigabytes = (bytes: number): string => `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;

const cancelledRead = (fileName: string, signal: AbortSignal): ChunkedAttachmentError =>
  new ChunkedAttachmentError('cancelled', `Reading "${fileName}" was cancelled.`, signal.reason);

/**
 * Settles as `pending` does, unless `signal` fires first — then rejects with
 * reason `cancelled` at once, without waiting for `pending`. This is what lets
 * a cancel surface while chunk reads that cannot be stopped are still on the
 * wire. A signal that has already fired rejects immediately. The listener is
 * removed once `pending` settles, so a long-lived signal collects no closures.
 */
const settleUnlessCancelled = <T>(pending: Promise<T>, signal: AbortSignal, fileName: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const onAbort = (): void => reject(cancelledRead(fileName, signal));
    if (signal.aborted) onAbort();
    signal.addEventListener('abort', onAbort, { once: true });
    void pending.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });

const readManifestText = async (manifestPath: string, fileName: string): Promise<string> => {
  try {
    const bytes = await getBytes(ref(storage, manifestPath), MAX_ATTACHMENT_MANIFEST_BYTES);
    return new TextDecoder().decode(bytes);
  } catch (error) {
    throw new ChunkedAttachmentError(
      'manifestUnreadable',
      `The chunk manifest for "${fileName}" could not be read from ${manifestPath}.`,
      error,
    );
  }
};

const readVerifiedChunk = async (
  fileUid: string,
  fileName: string,
  entry: AttachmentChunkEntry,
  index: number,
): Promise<Blob> => {
  const path = attachmentChunkPath(fileUid, entry.h);
  const bytes = await getBytes(ref(storage, path), MAX_STORED_ATTACHMENT_CHUNK_BYTES).catch(
    (error: unknown) => {
      throw new ChunkedAttachmentError(
        'chunkMissing',
        `Chunk ${index} of "${fileName}" is missing or unreadable at ${path}.`,
        error,
      );
    },
  );
  await verifyChunkBytes(entry, bytes, index);
  return new Blob([bytes]);
};

/**
 * Fetches every chunk into manifest-ordered blobs, `CHUNK_FETCH_CONCURRENCY` at
 * a time. The shared cursor is what bounds the pool: each worker takes the next
 * index until the list runs out, so slow chunks never idle a slot the way a
 * fixed-size batch would. The first rejection wins and the rest are abandoned —
 * a failed read has no partial result to salvage — and so does the caller's
 * signal firing, which rejects with `cancelled` at once.
 *
 * `abandoned` is what makes "abandoned" true rather than aspirational. Neither
 * `Promise.all` rejecting nor the signal firing can stop a worker mid-read, and
 * each would otherwise keep taking indices off the cursor: a poisoned chunk at
 * index 1 of a 500-chunk version would surface at once and still cost the
 * remaining 499 fetches. So a worker checks the predicate before taking an
 * index and again after its read lands, and a chunk that lands after the read
 * was abandoned is dropped rather than retained. The flag is raised on every
 * route out, not only a failed chunk fetch: an `onProgress` that throws would
 * otherwise reject the pool while leaving `failed` clear, and the remaining
 * workers would fetch and retain all 499. On any rejection `parts` is emptied on
 * the spot, so a straggling worker's closure holds no blob through it; the blob
 * store reclaims each chunk when the browser next collects garbage, which is the
 * strongest release the platform offers.
 */
const readVerifiedChunks = async (
  { fileUid, fileName, signal, onProgress }: ChunkedAttachmentVersionRequest,
  { chunks, fileSize }: AttachmentChunkManifest,
): Promise<Blob[]> => {
  const parts = new Array<Blob>(chunks.length);
  let nextIndex = 0;
  let failed = false;
  let bytesTransferred = 0;
  const abandoned = (): boolean => failed || signal?.aborted === true;
  const worker = async (): Promise<void> => {
    while (!abandoned() && nextIndex < chunks.length) {
      const index = nextIndex;
      nextIndex += 1;
      const part = await readVerifiedChunk(fileUid, fileName, chunks[index], index).catch(
        (error: unknown) => {
          failed = true;
          throw error;
        },
      );
      if (abandoned()) return;
      parts[index] = part;
      bytesTransferred += chunks[index].s;
      onProgress?.({ bytesTransferred, totalBytes: fileSize });
    }
  };
  const workers = Promise.all(
    Array.from({ length: Math.min(CHUNK_FETCH_CONCURRENCY, chunks.length) }, () => worker()),
  );
  try {
    await (signal === undefined ? workers : settleUnlessCancelled(workers, signal, fileName));
    return parts;
  } catch (error) {
    failed = true;
    parts.length = 0;
    throw error;
  }
};

/**
 * Assembles one chunked attachment version into a verified `Blob`. Throws
 * `ChunkedAttachmentError` on every path that returns no bytes — a failure, or
 * reason `cancelled` when `signal` fires — and returns nothing partial: no
 * caller ever sees unverified or short bytes.
 *
 * The signal is consulted before the manifest read, after it, before each chunk
 * is taken, after each chunk lands, and before the assembly, so a cancel never
 * starts work whose result would be thrown away.
 *
 * The manifest path is pinned to `versionId` before anything is fetched. A
 * manifest is version-private by construction — its path is derived entirely
 * from the reserved version id, and manifests are write-once — so a path that
 * names a different version means the caller assembled the request from two
 * different versions' fields, and the bytes it would return are some other
 * version's.
 */
export const readChunkedAttachmentVersion = async (
  request: ChunkedAttachmentVersionRequest,
): Promise<Blob> => {
  const { fileUid, versionId, manifestPath, fileName, contentType, signal, onProgress } = request;
  const throwIfCancelled = (): void => {
    if (signal?.aborted) throw cancelledRead(fileName, signal);
  };

  throwIfCancelled();

  const expectedManifestPath = attachmentManifestPath(fileUid, versionId);
  if (manifestPath !== expectedManifestPath) {
    throw new ChunkedAttachmentError(
      'manifestPathMismatch',
      `"${fileName}" names the manifest ${manifestPath}, which does not belong to version ${versionId} of this file (${expectedManifestPath}).`,
    );
  }

  const text = await readManifestText(manifestPath, fileName);
  throwIfCancelled();

  const manifest = (() => {
    try {
      return parseAttachmentChunkManifest(text);
    } catch (error) {
      throw new ChunkedAttachmentError(
        'manifestRefused',
        `The chunk manifest for "${fileName}" is not one this app can read: ${error instanceof Error ? error.message : String(error)}`,
        error,
      );
    }
  })();

  if (manifest.fileSize > MAX_ASSEMBLED_ATTACHMENT_BYTES) {
    throw new ChunkedAttachmentError(
      'tooLarge',
      `"${fileName}" is ${describeGigabytes(manifest.fileSize)}. This app assembles up to ${describeGigabytes(MAX_ASSEMBLED_ATTACHMENT_BYTES)} in the browser; use Syncretic File Sync on your computer to get a file this large.`,
    );
  }

  const encoded = manifest.chunks.find((entry) => entry.enc !== undefined);
  if (encoded !== undefined) {
    throw new ChunkedAttachmentError(
      'encodingUnsupported',
      `"${fileName}" has ${encoded.enc}-compressed chunks, which this app cannot decompress in the browser; use Syncretic File Sync on your computer to get this file.`,
    );
  }

  onProgress?.({ bytesTransferred: 0, totalBytes: manifest.fileSize });

  const parts = await readVerifiedChunks(request, manifest);
  if (signal?.aborted) {
    parts.length = 0;
    throw cancelledRead(fileName, signal);
  }
  const blob = new Blob(parts, { type: contentType });
  parts.length = 0;

  if (blob.size !== manifest.fileSize) {
    throw new ChunkedAttachmentError(
      'integrity',
      `"${fileName}" assembled to ${blob.size} bytes, but its manifest declares ${manifest.fileSize}.`,
    );
  }

  return blob;
};
